import React, { useEffect, useMemo, useState } from "react"
import { Container, Card, CardBody, Row, Col, Input, Label, Button, Spinner } from "reactstrap"
import { useNavigate, useParams } from "react-router-dom"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import EntityDocuments from "../../../components/Common/EntityDocuments"
import { getTenantCompanies } from "../../../services/tenancy"
import { postWorkforce, fetchWorkforceEmployee } from "../../../services/Hrms/workforce"
import { getTrades } from "../../../services/admin"
import { getCode } from "../../../services/inventory"
import { getEmployeeCampHistory } from "../../../services/campManagement"
import { toastError, toastSuccess } from "../../../helpers/msg_helper"

const CATEGORIES = ["WORKER", "STAFF"]
const PAY_TYPES = ["Hourly", "Basic"]
// Accommodation: physical camp groups + not-in-camp statuses (mirrors the HR sheet's CAMP column).
const CAMP_GROUPS = ["PARCO", "JEBEL ALI", "DIP", "STAFF"]
const CAMP_STATUSES = ["VACATION", "OUTSIDE", "ABSCOND", "CLIENT", "CANCEL"]

const empty = {
  company_id: "", amj_code: "", name: "", hired_trade: "", site_mobilized_trade: "",
  nationality: "", profession: "", person_code: "", date_of_birth: "", date_of_join: "",
  category: "WORKER", pay_type: "Hourly", mol_status: "", gdfra_status: "", camp: "",
  agent: "", wmc: "", remark: "", bank_account_no: "", employee_cif: "", employer_id: "",
  employer_ac_no: "", salary_card: "",
}

const EditWorkforce = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const companyname = localStorage.getItem("companyname")
  document.title = `Workforce${companyname ? ` | ${companyname}` : ""}`

  const [form, setForm] = useState(empty)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const [companiesRaw, setCompaniesRaw] = useState([])
  const [trades, setTrades] = useState([])
  const [codeEditable, setCodeEditable] = useState(false)
  const [documents, setDocuments] = useState({})
  const [docRecords, setDocRecords] = useState(null)
  const [campHistory, setCampHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // flatten the tenancy response (groups -> companies, plus standalone) into a flat option list
  const companies = useMemo(() => {
    const out = []
    const push = c => c && c.id && out.push(c)
    const walk = arr => (arr || []).forEach(item => {
      if (Array.isArray(item?.companies)) item.companies.forEach(push)
      else if (item?.company_name) push(item)
    })
    walk(companiesRaw)
    // de-dupe by id
    const seen = new Set()
    return out.filter(c => (seen.has(c.id) ? false : seen.add(c.id)))
  }, [companiesRaw])

  useEffect(() => {
    ;(async () => {
      try {
        const r = await getTenantCompanies()
        setCompaniesRaw(r?.data || r || [])
      } catch (e) { /* dropdown optional */ }
    })()
  }, [])

  // Trades master (common + per-company override). Re-fetched when the employee's company changes.
  useEffect(() => {
    ;(async () => {
      try {
        const r = await getTrades(form.company_id || undefined)
        setTrades((r?.data || []).filter(t => t.is_active))
      } catch (e) { /* dropdown optional */ }
    })()
  }, [form.company_id])

  // Auto-generate the Employee Code (prefix setting) for a new employee
  useEffect(() => {
    if (id) return
    ;(async () => {
      try {
        const r = await getCode("amj_code", "WorkforceEmployee", "Workforce Employee")
        const c = r?.code
        setForm(f => ({ ...f, amj_code: c?.code || "" }))
        setCodeEditable(c?.manualoverride == 1)
      } catch (e) { /* optional */ }
    })()
  }, [id])

  useEffect(() => {
    if (!id) return
    ;(async () => {
      setLoading(true)
      try {
        const r = await fetchWorkforceEmployee(id)
        const d = r?.data || {}
        const next = { ...empty }
        Object.keys(empty).forEach(k => { next[k] = d[k] ?? empty[k] })
        next.date_of_birth = d.date_of_birth ? String(d.date_of_birth).slice(0, 10) : ""
        next.date_of_join = d.date_of_join ? String(d.date_of_join).slice(0, 10) : ""
        next.company_id = d.company_id ?? ""
        setForm(next)
        setDocRecords(d.document_records || [])
      } catch (e) { toastError(e?.message || "Failed to load employee") }
      finally { setLoading(false) }
    })()
    ;(async () => {
      try { const h = await getEmployeeCampHistory(id); setCampHistory(h?.data || []) }
      catch (e) { /* history is optional */ }
    })()
  }, [id])

  const save = async () => {
    if (!form.name.trim()) return toastError("Name is required")
    setSaving(true)
    try {
      const payload = { ...form, ...(id ? { id } : {}), documents }
      delete payload.company_id // company comes from the active header scope, not a form field
      await postWorkforce(payload)
      toastSuccess(`Employee ${id ? "updated" : "created"} successfully`)
      navigate("/hrms-workforce")
    } catch (e) { toastError(e?.message || "Save failed") }
    finally { setSaving(false) }
  }

  const txt = (key, label, span = 3) => (
    <Col md={span} className="mb-3" key={key}>
      <Label>{label}</Label>
      <Input value={form[key] ?? ""} onChange={e => set(key, e.target.value)} />
    </Col>
  )

  // Trade dropdown from the maintained master. Preserves a legacy/free-text value that
  // isn't in the master (shown as "… (current)") so editing never silently drops it.
  const tradeSelect = (key, label, span = 3) => {
    const cur = form[key]
    const inMaster = cur && trades.some(t => (t.name || "").toLowerCase() === String(cur).toLowerCase())
    return (
      <Col md={span} className="mb-3" key={key}>
        <Label>{label}</Label>
        <Input type="select" value={form[key] ?? ""} onChange={e => set(key, e.target.value)}>
          <option value="">Select trade</option>
          {trades.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
          {cur && !inMaster && <option value={cur}>{cur} (current)</option>}
        </Input>
      </Col>
    )
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center">
          <Breadcrumbs title="HRMS" breadcrumbObject={[
            { title: "HRMS", link: "/hrms" },
            { title: "Workforce", link: "/hrms-workforce" },
            { title: id ? "Edit" : "New", link: "#" },
          ]} />
        </div>

        <Card className="invoice-main-card p-3 pt-4 ev-entity-form">
          <CardBody>
            {loading ? (
              <div className="text-center py-5"><Spinner /></div>
            ) : (
              <>
                <h5>Identity</h5>
                <Row>
                  <Col md={3} className="mb-3">
                    <Label>Employee Code</Label>
                    <Input value={form.amj_code ?? ""} disabled={!!id || !codeEditable}
                      placeholder="Auto" onChange={e => set("amj_code", e.target.value)} />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Label>Name <span className="text-danger">*</span></Label>
                    <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Full name" />
                  </Col>
                  {txt("nationality", "Nationality", 3)}
                  <Col md={3} className="mb-3">
                    <Label>Category</Label>
                    <Input type="select" value={form.category} onChange={e => set("category", e.target.value)}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </Input>
                  </Col>
                  <Col md={3} className="mb-3">
                    <Label>Pay Type</Label>
                    <Input type="select" value={form.pay_type} onChange={e => set("pay_type", e.target.value)}>
                      {PAY_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                    </Input>
                  </Col>
                  {txt("person_code", "Person Code", 3)}
                </Row>

                <hr className="my-3" />
                <h5>Employment</h5>
                <Row>
                  {tradeSelect("hired_trade", "Hired Trade", 3)}
                  {tradeSelect("site_mobilized_trade", "Site Mobilized Trade", 3)}
                  {txt("profession", "Profession", 3)}
                  <Col md={3} className="mb-3">
                    <Label>Date of Birth</Label>
                    <Input type="date" value={form.date_of_birth} onChange={e => set("date_of_birth", e.target.value)} />
                  </Col>
                  <Col md={3} className="mb-3">
                    <Label>Date of Join</Label>
                    <Input type="date" value={form.date_of_join} onChange={e => set("date_of_join", e.target.value)} />
                  </Col>
                  {txt("mol_status", "MOL Status", 3)}
                  {txt("gdfra_status", "GDFRA Status", 3)}
                  <Col md={3} className="mb-3">
                    <Label>Camp</Label>
                    <Input type="select" value={form.camp ?? ""} onChange={e => set("camp", e.target.value)}>
                      <option value="">Not assigned</option>
                      <optgroup label="Camp (housed)">
                        {CAMP_GROUPS.map(c => <option key={c} value={c}>{c}</option>)}
                      </optgroup>
                      <optgroup label="Not in camp (status)">
                        {CAMP_STATUSES.map(c => <option key={c} value={c}>{c}</option>)}
                      </optgroup>
                    </Input>
                  </Col>
                  {txt("agent", "Agent", 3)}
                  {txt("wmc", "WMC", 3)}
                </Row>

                <hr className="my-3" />
                <h5>Bank / Payroll</h5>
                <Row>
                  {txt("bank_account_no", "Bank Account No", 3)}
                  {txt("employee_cif", "Employee CIF", 3)}
                  {txt("employer_id", "Employer ID", 3)}
                  {txt("employer_ac_no", "Employer Ac No", 3)}
                  {txt("salary_card", "Salary Card", 3)}
                </Row>

                <Row>
                  <Col md={12} className="mb-3">
                    <Label>Remark</Label>
                    <Input type="textarea" rows={2} value={form.remark} onChange={e => set("remark", e.target.value)} />
                  </Col>
                </Row>

                <hr className="my-3" />
                <EntityDocuments
                  master="employee"
                  value={docRecords}
                  onChange={setDocuments}
                  baseUrl={process.env.REACT_APP_IMG_APIKEY}
                />

                {id && (
                  <>
                    <hr className="my-3" />
                    <h5>Camp History <small className="text-muted" style={{ fontWeight: 400 }}>— accommodation (camp / room / bed) over time</small></h5>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Camp</th><th>Room</th><th>Bed</th><th>Company</th><th>From</th><th>To</th><th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {campHistory.map(h => (
                            <tr key={h.id}>
                              <td>{h.camp || "—"}</td>
                              <td>{h.room_no || "—"}</td>
                              <td>{h.bed_no || "—"}</td>
                              <td>{h.company || "—"}</td>
                              <td>{h.from_date || "—"}</td>
                              <td>{h.to_date || "—"}</td>
                              <td>{h.current ? <span className="badge bg-success">Current</span> : null}</td>
                            </tr>
                          ))}
                          {!campHistory.length && <tr><td colSpan="7" className="text-center text-muted">No camp history.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                <div className="d-flex justify-content-end mt-3" style={{ gap: 8 }}>
                  <Button color="light" onClick={() => navigate("/hrms-workforce")}>Cancel</Button>
                  <Button color="primary" onClick={save} disabled={saving}>
                    {saving ? "Saving…" : id ? "Update" : "Save"}
                  </Button>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

export default EditWorkforce
